import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, User, AlertCircle, FolderOpen, FileText, Cloud, Shield, X, Mail } from 'lucide-react';
import '../styles/LoginPage.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../context/NotificationContext';
import BiometricAuthButton from '../components/BiometricAuthButton';
import logo from '../timefiles.png'

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [username2FA, setUsername2FA] = useState('');
  const [hasBiometric, setHasBiometric] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [emailCodeRequested, setEmailCodeRequested] = useState(false);

  const navigate = useNavigate();
  const { login, getUser, getCookie, submit2FA, isLoggingOut, user, authenticateWithBiometric, APP_URL } = useAuth();
  const { error: notifyError } = useNotify();

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        // Don't check auth if we're currently logging out
        if (isLoggingOut) {
          setAuthCheckComplete(true);
          return;
        }

        // First check if we have the required cookies
        const accessToken = getCookie('csrf_access_token');
        const refreshToken = getCookie('csrf_refresh_token');
        
        if (!accessToken && !refreshToken) {
          console.log('No authentication tokens found');
          setAuthCheckComplete(true);
          return;
        }

        // Only make the API call if we have tokens
        if (accessToken || refreshToken) {
          console.log('Authentication tokens found, checking user status...');
          const userData = await getUser({ cache: false });
          if (userData) {
            console.log('User already authenticated, redirecting');
            navigate('/');
            return;
          }
        }
      } catch (err) {
        console.log('Authentication check failed:', err.message);
        // If there's a network error or other issue, don't spam requests
        // Just let the user proceed with login
      } finally {
        setAuthCheckComplete(true);
      }
    };

    // Only run the check once
    if (!authCheckComplete) {
      checkExistingAuth();
    }
  }, [isLoggingOut, getCookie, getUser, navigate, authCheckComplete]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        if (result.requires2FA) {
          setUsername2FA(formData.username);
          setHasBiometric(result.hasBiometric || false);
          setHasEmail(result.hasEmail || false);

          // If no biometric, email was auto-sent by backend, so mark as requested
          if (!result.hasBiometric) {
            setEmailCodeRequested(true);
          } else {
            setEmailCodeRequested(false);
          }

          setShow2FAModal(true);
        } else {
          // Login successful without 2FA, redirect to dashboard
          console.log('Login successful, redirecting...');
          navigate('/');
        }
      } else {
        setError(result.error || 'Login failed');
        notifyError(result.error || 'Login failed');
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during login';
      setError(errorMessage);
      notifyError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    const code = e.target.elements.code.value;

    if (!code || code.length !== 8) {
      const errorMessage = 'Please enter a valid 8-digit authentication code';
      setError(errorMessage);
      notifyError(errorMessage);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await submit2FA(code);

      if (result.success) {
        // 2FA successful, redirect to dashboard
        setShow2FAModal(false);
        setLoading(false);
        navigate('/');
        console.log('2FA verification successful:', result);
      } else {
        const errorMessage = result.error || 'Invalid verification code';
        setError(errorMessage);
        notifyError(errorMessage);
        setLoading(false);
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during verification';
      setError(errorMessage);
      notifyError(errorMessage);
      console.error('2FA error:', err);
      setLoading(false);
    }
  };

  const handleRequestEmailCode = async () => {
    if (!username2FA || username2FA.trim().length === 0) {
      notifyError('Session expired, please login again');
      setShow2FAModal(false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${APP_URL}/login?action=request_2fa_code&username=${encodeURIComponent(username2FA)}`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setEmailCodeRequested(true);
      } else {
        notifyError(data.error || 'Failed to send email code');
      }
    } catch (err) {
      notifyError('An unexpected error occurred while requesting email code');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometric2FA = async () => {
    if (!username2FA || username2FA.trim().length === 0) {
      notifyError('Session expired, please login again');
      setShow2FAModal(false);
      return;
    }

    setLoading(true);

    try {
      const result = await authenticateWithBiometric(username2FA);

      if (result.success) {
        setShow2FAModal(false);
        setLoading(false);
        navigate('/');
      } else {
        notifyError(result.error || 'Biometric authentication failed');
        setLoading(false);
      }
    } catch (err) {
      notifyError('An unexpected error occurred during biometric authentication');
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && formData.username && formData.password) {
      handleSubmit(e);
    }
  };

  // Don't render login form until auth check is complete
  if (!authCheckComplete) {
    return (
      <div className="lg-login-container">
        <div className="lg-login-content">
          <div className="lg-login-header">
            <h1 className="lg-app-title">TimeFiles</h1>
            <p className="lg-app-subtitle">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render login page if user is already authenticated
  if (user) {
    navigate('/');
    return null;
  }

  return (
    <div className="lg-login-container">
      {/* Background Pattern */}
      <div className="lg-background-pattern">
        <div className="lg-bg-icon lg-bg-icon-1">
          <FolderOpen />
        </div>
        <div className="lg-bg-icon lg-bg-icon-2">
          <FileText />
        </div>
        <div className="lg-bg-icon lg-bg-icon-3">
          <Cloud />
        </div>
        <div className="lg-bg-icon lg-bg-icon-4">
          <FolderOpen />
        </div>
      </div>

      <div className="lg-login-content">
        {/* Header */}
        <div className="lg-login-header">
          <div className="lg-logo-container">
            <div className="lg-logo-icons">
              <img src={logo}/>
            </div>
          </div>
          <h1 className="lg-app-title">
            TimeFiles
          </h1>
          <p className="lg-app-subtitle">
            When the work is done  
          </p>
          {/* <p className="lg-app-description">
            Secure File Storage
          </p> */}
        </div>

        {/* Login Card */}
        <div className="lg-login-card">
          {/* Card Background Pattern */}
          <div className="lg-card-background">
            <div className="lg-card-bg-icon lg-card-bg-icon-1">
              <FolderOpen />
            </div>
            <div className="lg-card-bg-icon lg-card-bg-icon-2">
              <FileText />
            </div>
          </div>

          <div className="lg-form-content">
            {/* Security Badge */}
            <div className="lg-security-badge">
              <Shield className="lg-security-icon" />
              <span className="lg-security-text">Encrypted & Secure</span>
            </div>

            {/* Error Message */}
            {error && (
              <div className="lg-error-container">
                <AlertCircle className="lg-error-icon" />
                <span className="lg-error-text">{error}</span>
              </div>
            )}

            {/* Username Field */}
            <div className="lg-field-container">
              <label htmlFor="username" className="lg-field-label">
                Username
              </label>
              <div className="lg-field-input-container">
                <User className="lg-field-icon" />
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="lg-field-input"
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="lg-field-container">
              <label htmlFor="password" className="lg-field-label">
                Password
              </label>
              <div className="lg-field-input-container">
                <Lock className="lg-field-icon" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="lg-field-input lg-password-input"
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="lg-password-toggle"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="lg-password-toggle-icon" />
                  ) : (
                    <Eye className="lg-password-toggle-icon" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div className="lg-submit-container">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !formData.username || !formData.password}
                className="lg-submit-button"
              >
                {loading ? (
                  <div className="lg-loading-content">
                    <div className="lg-loading-spinner"></div>
                    <span>Accessing your files...</span>
                  </div>
                ) : (
                  <div className="lg-button-content">
                    <span>Access TimeFiles</span>
                    <FolderOpen className="lg-button-icon" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="lg-features-grid">
          <div className="lg-feature-card">
            <Cloud className="lg-feature-icon lg-feature-icon-cloud" />
            <p className="lg-feature-text">Cloud Storage</p>
          </div>
          <div className="lg-feature-card">
            <Shield className="lg-feature-icon lg-feature-icon-shield" />
            <p className="lg-feature-text">Secure Access</p>
          </div>
          <div className="lg-feature-card">
            <FileText className="lg-feature-icon lg-feature-icon-file" />
            <p className="lg-feature-text">File Management</p>
          </div>
        </div>

        {/* Footer */}
        <div className="lg-footer">
          <p className="lg-footer-text">
            Powered by ClockwiseCPA • Enterprise File Storage
          </p>
        </div>
      </div>

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="lg-modal-overlay">
          <div className="lg-modal-container">
            <button
              className="lg-modal-close"
              onClick={() => setShow2FAModal(false)}
              disabled={loading}
            >
              <X className="lg-modal-close-icon" />
            </button>

            <div className="lg-modal-header">
              <div className="lg-modal-logo">
                <Shield className="lg-modal-shield-icon" />
              </div>
              <h2 className="lg-modal-title">Two-Factor Authentication</h2>
              <p className="lg-modal-subtitle">
                {hasBiometric && !emailCodeRequested
                  ? 'Use biometric or request email code'
                  : emailCodeRequested
                  ? 'Enter the code sent to your email'
                  : 'Choose your verification method'}
              </p>
            </div>

            <div className="lg-modal-content">
              {error && (
                <div className="lg-error-container">
                  <AlertCircle className="lg-error-icon" />
                  <span className="lg-error-text">{error}</span>
                </div>
              )}

              <form onSubmit={handle2FASubmit} className="lg-modal-form">
                {/* Show biometric first if available and email not requested */}
                {hasBiometric && !emailCodeRequested && (
                  <>
                    <BiometricAuthButton
                      onSuccess={handleBiometric2FA}
                      onError={(err) => notifyError(err.message || 'Biometric authentication failed')}
                      disabled={loading}
                      username={username2FA}
                    />

                    {hasEmail && (
                      <>
                        <div className="lg-2fa-divider">
                          <div className="lg-2fa-divider-line"></div>
                          <span className="lg-2fa-divider-text">OR</span>
                          <div className="lg-2fa-divider-line"></div>
                        </div>

                        <button
                          type="button"
                          className="lg-submit-button lg-email-code-btn"
                          onClick={handleRequestEmailCode}
                          disabled={loading}
                        >
                          <div className="lg-button-content">
                            <Mail className="lg-button-icon" />
                            <span>Email Verification</span>
                          </div>
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Show email code input if no biometric or email was requested */}
                {(!hasBiometric || emailCodeRequested) && hasEmail && (
                  <>
                    <div className="lg-field-container">
                      <label htmlFor="code" className="lg-field-label">
                        Email Code
                      </label>
                      <div className="lg-field-input-container">
                        <Shield className="lg-field-icon" />
                        <input
                          type="text"
                          id="code"
                          name="code"
                          placeholder="00000000"
                          className="lg-field-input lg-2fa-input"
                          autoComplete="off"
                          maxLength="8"
                          disabled={loading}
                        />
                      </div>
                      <small className="lg-field-hint">
                        {emailCodeRequested
                          ? 'Check your email for the 8-digit code'
                          : 'Enter the 8-digit code sent to your email'}
                      </small>
                    </div>

                    <div className="lg-submit-container">
                      <button
                        type="submit"
                        className="lg-submit-button"
                        disabled={loading}
                      >
                        {loading ? (
                          <div className="lg-loading-content">
                            <div className="lg-loading-spinner"></div>
                            <span>Verifying...</span>
                          </div>
                        ) : (
                          <div className="lg-button-content">
                            <span>Verify Code</span>
                            <Shield className="lg-button-icon" />
                          </div>
                        )}
                      </button>
                    </div>

                    {hasBiometric && emailCodeRequested && (
                      <>
                        <div className="lg-2fa-divider">
                          <div className="lg-2fa-divider-line"></div>
                          <span className="lg-2fa-divider-text">OR</span>
                          <div className="lg-2fa-divider-line"></div>
                        </div>

                        <BiometricAuthButton
                          onSuccess={handleBiometric2FA}
                          onError={(err) => notifyError(err.message || 'Biometric authentication failed')}
                          disabled={loading}
                          username={username2FA}
                        />
                      </>
                    )}
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;